import { Component, HostListener, AfterViewInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WizardPageComponent } from '../../../../../core/wizard/wizard-page-component';
import type { ComprehensiveRankingTableWizard } from '../comprehensive-ranking-table-wizard';
import { Store } from '../../../../../core/models';
import { WizardRunnerService } from 'src/app/core/wizard/wizard-runner.service';
import { RankByStoreWizard } from '../../rank-by-store-wizards/rank-by-store-wizard';
import { RankByStorDetailReportWizard } from '../../rank-by-store-wizards/rank-by-store-detail-wizard';
// import { SalesVolumeFilterUIData } from './sales-volume-filter-ui/sales-volume-filter-ui.component';
import { SalesVolumeRangeData, UniversalFilterData, FilterCategoryKey } from './sales-volume-range-filter-dialog/sales-volume-range-filter-dialog.component';
import { SalesVolumeRangeFilterWizard } from './sales-volume-range-filter-dialog/sales-volume-range-filter-wizard';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
import { DetailState, selectDetailState, selectvolumeDetailState } from 'src/app/detail/detail-state';
import { Store as NgrxStore } from '@ngrx/store';
import { AppState } from 'src/app/app-state';
import { FipsService } from 'src/app/core/fips/fips.service';
import { DefinitionService } from 'src/app/core/definition/definition.service';
import { DynamicPdfService, RankingTablePdfRow } from 'src/app/core/service/dynamic-pdf.service';
import { FileDownloadService } from 'src/app/core/file/file-download.service';
import * as ExcelJS from 'exceljs';
import { StoreRankingService } from 'src/app/core/store/ranking/store-ranking.service';
import { StoreVolume } from 'src/app/core/store/volume/store-volume';
import { formatAsCurrency } from 'src/app/core/util/string-utils';
import { FitType } from 'src/app/core/store/fit/fit-type.enum';
import { take, switchMap, catchError, map, finalize } from 'rxjs/operators';
import { Subject, of, forkJoin, Observable } from 'rxjs';
import { LogoUtil } from 'src/app/core/util/logo-util';
import { selectUserState } from 'src/app/auth/user-state';
import { SalesSqftDisplayType } from 'src/app/core/user-preferences/sales-sqft-display-type.enum';

// Band values returned by the API for badge colouring
type BandValue = 'TOP' | 'GOOD' | 'AVERAGE' | 'BELOW_AVERAGE' | 'POOR' | string;

interface RankingTableData {
  name: string;
  storeNumber?: string;
  address: string;
  storeType: 'selected' | 'banner' | 'other';
  annualSales: string;
  fitType: string;
  status: string;
  sqft: number;
  // GRO-711: straight-line distance in miles from the currently viewed store.
  // null when the API omits it; the subject store's own row is rendered as a dash.
  distanceMiles: number | null;
  storeUuid?: string;
  bannerName?: string;

  // ── Banner/Company ranks — shown in the MAIN TABLE (vs same-banner stores)
  // Source: national_rank_banner, state_rank_banner, etc.
  nationalRank: number;
  nationalRankTotal?: number;
  nationalBand?: BandValue;
  stateRank: number;
  stateRankTotal?: number;
  stateBand?: BandValue;
  countyRank: number;
  countyRankTotal?: number;
  countyBand?: BandValue;
  msaRank: number;
  msaRankTotal?: number;
  msaBand?: BandValue;
  miles: number;
  milesTotal?: number;
  milesBand?: BandValue;
  minutes: number;
  minutesTotal?: number;
  minutesBand?: BandValue;

  // ── Industry ranks — shown in wizard popup (vs all grocery stores)
  // Source: national_rank_industry, national_band_industry, etc.
  industryNationalRank?: number;
  industryNationalRankTotal?: number;
  industryNationalBand?: BandValue;
  industryStateRank?: number;
  industryStateRankTotal?: number;
  industryStateBand?: BandValue;
  industryCountyRank?: number;
  industryCountyRankTotal?: number;
  industryCountyBand?: BandValue;
  industryMsaRank?: number;
  industryMsaRankTotal?: number;
  industryMsaBand?: BandValue;
  industryMiles?: number;
  industryMilesTotal?: number;
  industryMilesBand?: BandValue;
  industryMinutes?: number;
  industryMinutesTotal?: number;
  industryMinutesBand?: BandValue;

  stateKey?: string;   // e.g. item.state ("CA", "TX")
  countyKey?: string;  // e.g. item.county or item.county_name
  cbsaKey?: string;    // e.g. item.cbsa_code or item.cbsa

  isSelected?: boolean;
  isHovered?: boolean;
}

@Component({
  standalone: false,
  selector: 'mtn-comprehensive-ranking-table-page',
  templateUrl: './comprehensive-ranking-table-page.component.html',
  styleUrls: ['./comprehensive-ranking-table-page.component.scss']
})
export class ComprehensiveRankingTablePageComponent extends WizardPageComponent<ComprehensiveRankingTableWizard> implements AfterViewInit {

  key = 'comprehensive-ranking-table';
  store: Store;

  // Store detail display values for export
  storeStatusDisplay = 'N/A';
  fitCategoryDisplay = 'N/A';
  estSalesVolume = 'Not Available';
  estSalesSqft = 'Not Available';

  // Filter dropdowns state
  filterLocationDropdownOpen = false;
  exportDropdownOpen = false;
  paginationDropdownOpen = false;
  showSalesVolumeFilter = false;
  milesDropdownOpen = false;
  minutesDropdownOpen = false;

  // Persists across popup open/close within the same session
  appliedFilterData: UniversalFilterData | null = null;

  // Toggle states
  showAsAnnualSales = false;
  selectedStoreAlwaysOnTop = true;

  // GRO-911: the user's Sales SQFT preference decides which area the Store Size column shows.
  // Defaults to total area, as the rest of the ranking views do when no preference is set.
  salesSqftDisplayType: SalesSqftDisplayType = SalesSqftDisplayType.TOTAL_AREA;

  // Selected values
  selectedFilterLocation = 'Filter Location';
  selectedExportOption = 'Export';
  selectedPaginationOption = '10';
  // GRO-712: default trade-area filters are 5 miles / 7 minutes
  selectedMilesOption = '5';
  selectedMinutesOption = '7';

  previousselectedMilesOption = this.selectedMilesOption;
  previousselectedMinutesOption = this.selectedMinutesOption;

  // Pagination properties (server-side, 0-indexed to match API)
  currentPage = 0;
  totalPages = 0;
  pageSize = 10;
  totalItems = 0;

  // Loading / error state
  isLoading = false;
  loadingError: string | null = null;

  // Export-only loading guard (independent of the on-screen table state)
  isExporting = false;

  // Coverage metadata from API (populated from first response item)
  coveragePct: number | null = null;
  filteredStoreCount: number | null = null;
  totalBannerStoreCount: number | null = null;

  // Sorting properties
  sortColumn: string | null = 'nationalRank';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Dropdown options — 1 to 20 (max 20 per API constraint)
  milesOptions: string[]   = Array.from({ length: 20 }, (_, i) => String(i + 1));
  minutesOptions: string[] = Array.from({ length: 20 }, (_, i) => String(i + 1));

  // Emits params for each API call; switchMap auto-cancels the previous in-flight request
  private readonly apiTrigger$ = new Subject<{ payload: any; page: number; size: number; sort?: string }>();

  // Snapshot of current page for export
  private allTableData: RankingTableData[] = [];

  // Current page data
  tableData: RankingTableData[] = [];

  // GRO-713/715: when navigated from the "vs All Grocers" ring with ?openStoreDetail=1,
  // auto-open the selected store's Location Details popup once data has loaded.
  private autoOpenStoreDetail = false;
  private hasAutoOpenedDetail = false;

  // GRO-711: fit systemName -> displayName, so the FIT column shows friendly names
  // (e.g. SUPERCENTER -> Supercenter) consistent with the rest of the app.
  private fitDisplayByKey: Record<string, string> = {};

  constructor(private wizardRunner: WizardRunnerService,
              private hitTrackingService: HitTrackingService,
              protected ngrxStore: NgrxStore<AppState>,
              private fipsService: FipsService,
              private definitionService: DefinitionService,
              private dynamicPdfService: DynamicPdfService,
              private fileDownloadService:FileDownloadService,
              private storeRankingService: StoreRankingService,
              private route: ActivatedRoute
  ) {
    super();
    this.title = 'Store Ranking - Detailed Report';
    this.closeButtonText = 'Close';
    this.autoOpenStoreDetail = this.route.snapshot.queryParamMap.get('openStoreDetail') === '1';
    this.setupApiPipeline();
    this.preloadFilterData();
    this.subscribeToDetailState();
    this.subscribeToSalesSqftPreference();
    this.setupFilterEventListener();
  }

  /** Header suffix for the Store Size column, matching the area the API was asked for. */
  get storeSizeLabel(): string {
    return this.salesSqftDisplayType === SalesSqftDisplayType.SALES_AREA ? 'Sales Area SQFT' : 'Total SQFT';
  }

  /**
   * GRO-911: the server sets square_footage to the area this preference names. The first emission
   * arrives before onLoad sets the store, so only a later change of preference triggers a reload.
   */
  private subscribeToSalesSqftPreference(): void {
    this.addSubscription(
      selectUserState(this.ngrxStore).subscribe(userState => {
        const displayType = userState?.userPreferences?.salesSqftDisplayMode || SalesSqftDisplayType.TOTAL_AREA;
        if (displayType !== this.salesSqftDisplayType) {
          this.salesSqftDisplayType = displayType;
          if (this.store) {
            this.loadComprehensiveRankingData();
          }
        }
      })
    );
  }

  /**
   * Set up event listener for filter applied events from the filter dialog
   */
  private setupFilterEventListener(): void {
    window.addEventListener('filterApplied', (event: any) => {
      const { filterData, resultCountBefore, callback } = event.detail;
      
      console.log('Filter applied - result count before:', resultCountBefore);
      
      // Apply the filter data
      this.appliedFilterData = filterData;
      this.currentPage = 0;
      
      // Load data and get result count after
      this.loadComprehensiveRankingData();
      
      // Wait for API call to complete and then call the callback with new result count
      setTimeout(() => {
        callback(this.totalItems);
      }, 1500); // Wait 1.5 seconds for API call to complete
    });
  }

  /**
   * Set up once: switchMap on apiTrigger$ so every new emission cancels
   * any still-running HTTP request before starting the next one.
   */
  private setupApiPipeline(): void {
    this.addSubscription(
      this.apiTrigger$.pipe(
        switchMap(({ payload, page, size, sort }) =>
          this.storeRankingService.getComprehensiveRanking(payload, page, size, sort).pipe(
            // Catch error INSIDE switchMap so the outer stream stays alive for future calls
            catchError((error) => {
              this.isLoading = false;
              this.tableData    = [];
              this.allTableData = [];
              this.totalItems   = 0;
              this.totalPages   = 0;
              if (!(error?.status === 404 || error?.message?.toLowerCase().includes('not found'))) {
                this.loadingError = error.message || 'Failed to load ranking data';
                console.error('Error loading comprehensive ranking data:', error);
              }
              return of(null); // emit null so the outer stream keeps going
            })
          )
        )
      ).subscribe((response) => {
        if (response === null) return; // already handled by catchError above
        this.isLoading = false;
        try {
          this.processApiResponse(
            response ?? { data: [], pagination: { total_count: 0, total_pages: 0 } }
          );
        } catch (e) {
          console.error('Error processing ranking response:', e);
          this.tableData    = [];
          this.allTableData = [];
          this.totalItems   = 0;
          this.totalPages   = 0;
        }
      })
    );
  }

  private preloadFilterData(): void {
    this.fipsService.findAllStates().subscribe();
    this.fipsService.findAllCbsas().subscribe();
    this.definitionService.findAllFits().subscribe((fits: any[]) => {
      const map: Record<string, string> = {};
      (fits ?? []).forEach((f: any) => {
        if (f?.systemName != null && f?.displayName) {
          map[String(f.systemName)] = f.displayName;
        }
      });
      this.fitDisplayByKey = map;
    });
  }

  /**
   * GRO-711: friendly display for a store fit category. Uses the definition
   * displayName (same source the rest of the app uses); falls back to a title-cased
   * version of the raw key, and 'N/A' when absent.
   */
  getFitDisplay(fit: string): string {
    if (!fit || fit === '—') {
      return 'N/A';
    }
    return this.fitDisplayByKey[fit] || this.titleCaseFit(fit);
  }

  private titleCaseFit(value: string): string {
    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  /**
   * GRO-711: distance in miles from the currently viewed store, thousands-separated so
   * it reads like the Store Size column ("25,000"). The unit lives in the column header,
   * so the cell carries the bare number.
   * The subject store's own row shows a dash; rows the API has no distance for show N/A.
   */
  getDistanceDisplay(item: RankingTableData): string {
    const miles = this.getDistanceMiles(item);
    if (miles == null) {
      return item.isSelected ? '—' : 'N/A';
    }
    return this.formatMilesWithSeparators(miles);
  }

  /**
   * Groups the integer part US-style and leaves the decimals exactly as the API sent
   * them — 1450.7234 → "1,450.7234", 12.5 → "12.5", 8 → "8". Only the digits before
   * the point go through Intl, so nothing is ever rounded off the end of the value.
   */
  private formatMilesWithSeparators(miles: number): string {
    const [whole, fraction] = String(miles).split('.');
    const grouped = Number(whole).toLocaleString('en-US');
    return fraction ? `${grouped}.${fraction}` : grouped;
  }

  /**
   * Export value. Excel takes the raw number (it formats the column itself via numFmt,
   * so the cell still sorts and aggregates); CSV takes the formatted text, matching the
   * other pre-formatted columns in that file. Missing values fall back to the same
   * markers, with a plain hyphen — an em dash is multi-byte in UTF-8 and renders as
   * mojibake in spreadsheet tools that ignore the CSV byte-order mark.
   */
  private getDistanceExportValue(item: RankingTableData, numeric: boolean): number | string {
    const miles = this.getDistanceMiles(item);
    if (miles == null) {
      return item.isSelected ? '-' : 'N/A';
    }
    return numeric ? miles : this.getDistanceDisplay(item);
  }

  /**
   * Store size for exports — thousands-separated for CSV ("25,000"), raw for Excel.
   * The API sends 0 for stores with no square footage on file; those read as N/A,
   * matching the on-screen cell.
   */
  private getStoreSizeExportValue(item: RankingTableData, numeric: boolean): number | string {
    if (!item.sqft) {
      return 'N/A';
    }
    return numeric ? Number(item.sqft) : Number(item.sqft).toLocaleString('en-US');
  }

  /**
   * The API's distance value as-is — no rounding, so whatever precision it sends is
   * what gets displayed and exported. Null when absent or not applicable.
   */
  private getDistanceMiles(item: RankingTableData): number | null {
    if (item.isSelected || item.distanceMiles == null) {
      return null;
    }
    const miles = Number(item.distanceMiles);
    return isNaN(miles) ? null : miles;
  }

  onLoad() {
    this.store = this.wizard.model.store;
    this.loadComprehensiveRankingData();
    return super.onLoad();
  }

  /**
   * Builds the API payload from current store state, filters, miles, and minutes.
   * Endpoint: POST /api/insights/rank-by-store/comprehensive?page=0&size=10
   */
  private buildApiPayload(): any {
    const location = this.store?.space?.location;
    const latitude = location?.getPointLatitude();
    const longitude = location?.getPointLongitude();

    const payload: any = {
      driveTime: parseInt(this.selectedMinutesOption, 10),
      miles: parseInt(this.selectedMilesOption, 10),
      salesVolumeDisplayType: this.showAsAnnualSales ? 'ANNUAL' : 'WEEKLY',
      salesSqftDisplayType: this.salesSqftDisplayType,
      longitude,
      latitude,
      storeId: this.store.id,
      // GRO-865: the server sorts and paginates the whole result set for the FIT, Size, Distance,
      // Miles and Minutes columns, so it has to know about the pin. Left to itself it would sort the
      // subject store into its natural position — off page one entirely, and always last for Distance,
      // where the store has no distance from itself. Sorting the page here cannot recover a row the
      // page never contained.
      selectedStoreAlwaysOnTop: this.selectedStoreAlwaysOnTop,
    };

    // Build filter object
    const filter: any = {};

    // Always include the current store's banner ID so the API can mark it
    if (this.store?.banner?.id) {
      console.log("🚀 ~ ComprehensiveRankingTablePageComponent ~ buildApiPayload ~ this.store?.banner?.id:", this.store?.banner?.id)
      filter.bannerIds = [this.store.banner.id];
    }

    
    if (this.appliedFilterData) {
      const d = this.appliedFilterData;

      if (d.salesVolume?.range) {
        filter.salesVolumeMin = d.salesVolume.range.minValue;
        filter.salesVolumeMax = d.salesVolume.range.maxValue;
      }

      if (d.sqft?.range) {
        filter.sqftMin = d.sqft.range.minValue;
        filter.sqftMax = d.sqft.range.maxValue;
      }

      if (d.geography?.states?.length) {
        filter.states = d.geography.states;
      }

      // CBSAs = explicitly-selected CBSAs plus those implied by any selected unsubscribed
      // ("derived") state. Only subscribed CBSAs are ever included, so entitlement is preserved.
      const cbsas = Array.from(new Set([
        ...(d.geography?.msas || []),
        ...(d.geography?.derivedCbsas || []),
      ]));
      if (cbsas.length) {
        filter.cbsas = cbsas;
      }

      // FIT categories stored as company.banners in the UI filter dialog
      if (d.company?.banners?.length) {
        filter.fits = d.company.banners;
      }
    }

    if (Object.keys(filter).length > 0) {
      payload.filter = filter;
    }

    return payload;
  }

  /** Triggers a new API fetch; any previous in-flight request is auto-cancelled. */
  loadComprehensiveRankingData(): void {
    if (!this.store) return;
    this.isLoading    = true;
    this.loadingError = null;
    this.apiTrigger$.next({
      payload: this.buildApiPayload(),
      page:    this.currentPage,
      size:    this.pageSize,
      sort:    this.buildSortParam(),
    });
  }

  /**
   * Response formats accepted:
   *   Spring Page:  { content: [], totalElements: N, totalPages: N, number: N, size: N }
   *   Custom:       { data: [], total: N, totalPages: N, page: N }
   *   Paginated:    { data: [], pagination: { total_count: N, total_pages: N } }
   *   Bare array:   []
   *   null / empty: treated as zero results (never throws)
   */
  private processApiResponse(response: any): void {
    if (response == null) {
      this.tableData = []; this.allTableData = []; this.totalItems = 0; this.totalPages = 0;
      return;
    }

    // ── Extract items ──────────────────────────────────────────────────────
    const items: any[] =
      Array.isArray(response?.content) ? response.content :
      Array.isArray(response?.data)    ? response.data    :
      Array.isArray(response)          ? response         : [];

    // ── Extract server-side totals (prefer API values, fall back to inference) ──
    const apiTotalElements: number | null =
      response?.pagination?.total_count ??
      response?.totalElements ?? response?.total_elements ?? response?.total ?? null;

    const apiTotalPages: number | null =
      response?.pagination?.total_pages ??
      response?.totalPages ?? response?.total_pages ?? null;

    if (items.length === 0) {
      this.tableData         = [];
      this.allTableData      = [];
      this.totalItems        = apiTotalElements ?? 0;
      this.totalPages        = apiTotalPages    ?? 0;
      this.coveragePct       = null;
      this.filteredStoreCount = null;
      this.totalBannerStoreCount = null;
      return;
    }

    // ── Coverage metadata (same on every row — read from first item) ───────
    const first = items[0];
    this.coveragePct           = first.banner_coverage_pct        ?? null;
    this.filteredStoreCount    = first.filtered_banner_store_count ?? null;
    this.totalBannerStoreCount = first.total_banner_store_count    ?? null;

    // ── Pagination totals ──────────────────────────────────────────────────
    if (apiTotalElements != null) {
      this.totalItems  = apiTotalElements;
      this.totalPages  = apiTotalPages ?? Math.ceil(apiTotalElements / this.pageSize);
    } else {
      // Inference fallback: if a full page came back there are likely more
      const hasMorePages   = items.length >= this.pageSize;
      this.totalItems      = this.currentPage * this.pageSize + items.length;
      this.totalPages      = hasMorePages ? this.currentPage + 2 : this.currentPage + 1;
    }

    this.tableData    = this.dedupeByStoreUuid(
      items.map((item: any) => this.mapResponseItemToTableData(item))
    );
    this.allTableData = [...this.tableData];
    this.applySortingOnCurrentPage();
    this.maybeAutoOpenStoreDetail();
  }

  /**
   * GRO-713/715: if opened from the "vs All Grocers" ring (?openStoreDetail=1), open
   * the selected store's Location Details popup once the data has loaded and the
   * selected row is present. Runs at most once.
   */
  private maybeAutoOpenStoreDetail(): void {
    if (!this.autoOpenStoreDetail || this.hasAutoOpenedDetail) {
      return;
    }
    const selected = this.tableData.find((t) => t.isSelected);
    if (selected) {
      this.hasAutoOpenedDetail = true;
      this.openDetailReport(selected);
    }
  }

  /** Maps one API row to RankingTableData using exact response field names. */
  private mapResponseItemToTableData(item: any): RankingTableData {
    const isSelected = !!item.store_uuid && item.store_uuid === this.store?.uuid;
    const isBanner   = !isSelected && item.banner_id === this.store?.banner?.id;

    const address = [item.address_line_1, item.state, item.zip_code]
      .filter(Boolean).join(', ');

    const volume = item.store_volume;
    const annualSales = volume != null ? this.formatVolume(volume) : 'N/A';

    return {
      name:       item.store_name   ?? 'Unknown',
      storeNumber: item.store_number != null ? String(item.store_number) : undefined,
      address,
      storeUuid:  item.store_uuid   ?? undefined,
      bannerName: item.banner_name  ?? undefined,
      storeType:  isSelected ? 'selected' : (isBanner ? 'banner' : 'other'),
      annualSales,
      fitType: item.store_fit_type ?? '—',
      status:  item.current_status ?? '—',
      sqft:    item.square_footage ?? 0,
      distanceMiles: item.distance_miles ?? null,

      // ── Banner/Company ranks → MAIN TABLE (vs same-banner stores) ──────────
      nationalRank:      item.national_rank_banner            ?? 0,
      nationalRankTotal: item.total_count_banner_national     ?? undefined,
      nationalBand:      item.national_band_banner            ?? undefined,

      stateRank:         item.state_rank_banner               ?? 0,
      stateRankTotal:    item.total_count_banner_state        ?? undefined,
      stateBand:         item.state_band_banner               ?? undefined,

      countyRank:        item.county_rank_banner              ?? 0,
      countyRankTotal:   item.total_count_banner_county       ?? undefined,
      countyBand:        item.county_band_banner              ?? undefined,

      msaRank:           item.cbsa_rank_banner                ?? 0,
      msaRankTotal:      item.total_count_banner_cbsa         ?? undefined,
      msaBand:           item.cbsa_band_banner                ?? undefined,

      miles:             item.miles_banner                    ?? 0,
      milesTotal:        item.total_count_banner_miles        ?? undefined,
      milesBand:         item.miles_band_banner               ?? undefined,

      minutes:           item.drive_time_banner               ?? 0,
      minutesTotal:      item.total_count_banner_drive_time   ?? undefined,
      minutesBand:       item.drive_time_band_banner          ?? undefined,

      // ── Industry ranks → DETAIL REPORT popup (vs all grocery stores) ────
      industryNationalRank:      item.national_rank_industry          ?? undefined,
      industryNationalRankTotal: item.total_count_national            ?? undefined,
      industryNationalBand:      item.national_band_industry          ?? undefined,

      industryStateRank:         item.state_rank_industry             ?? undefined,
      industryStateRankTotal:    item.total_count_state               ?? undefined,
      industryStateBand:         item.state_band_industry             ?? undefined,

      industryCountyRank:        item.county_rank_industry            ?? undefined,
      industryCountyRankTotal:   item.total_count_county              ?? undefined,
      industryCountyBand:        item.county_band_industry            ?? undefined,

      industryMsaRank:           item.cbsa_rank_industry              ?? undefined,
      industryMsaRankTotal:      item.total_count_cbsa                ?? undefined,
      industryMsaBand:           item.cbsa_band_industry              ?? undefined,

      industryMiles:             item.miles_industry                  ?? undefined,
      industryMilesTotal:        item.total_count_industry_miles      ?? undefined,
      industryMilesBand:         item.miles_band_industry             ?? undefined,

      industryMinutes:           item.drive_time_industry             ?? undefined,
      industryMinutesTotal:      item.total_count_industry_drive_time ?? undefined,
      industryMinutesBand:       item.drive_time_band_industry        ?? undefined,

      stateKey:  item.state        ?? undefined,
      countyKey: item.county       ?? item.county_name  ?? undefined,
      cbsaKey:   item.cbsa_code    ?? item.cbsa         ?? item.cbsa_name ?? undefined,

      isSelected,
    };
  }

  /**
   * Compact currency formatter: $50,960,000 → "$51.0M", $634,000 → "$634K"
   */
  private formatVolume(value: number): string {
    if (value >= 1_000_000) {
      // Use up to 2 decimal places; parseFloat trims trailing zeros (2.98M not 2.980M, 3M not 3.00M)
      return `$${parseFloat((value / 1_000_000).toFixed(2))}M`;
    }
    if (value >= 1_000) {
      return `$${(value / 1_000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  }

  /**
   * Row comparator shared by the on-screen page and the full export set so both
   * order rows identically (selected store on top, N/A ranks last, then by column).
   */
  private compareRankingRows = (a: RankingTableData, b: RankingTableData): number => {
    if (this.selectedStoreAlwaysOnTop) {
      if (a.isSelected && !b.isSelected) return -1;
      if (!a.isSelected && b.isSelected) return 1;
    }

    // GRO-865: Store FIT Category is the only non-numeric sort — it orders on the
    // category name as displayed, so A–Z means what the analyst reads in the cell.
    if (this.sortColumn === 'fitType') {
      return this.compareFitCategory(a, b);
    }

    // N/A (rank=0) rows always sort to the bottom regardless of sort direction
    const rankOrMax = (v: number) => (v == null || v === 0) ? Infinity : v;

    let aValue: number;
    let bValue: number;

    switch (this.sortColumn) {
      case 'nationalRank': aValue = rankOrMax(a.nationalRank); bValue = rankOrMax(b.nationalRank); break;
      case 'stateRank':    aValue = rankOrMax(a.stateRank);    bValue = rankOrMax(b.stateRank);    break;
      case 'countyRank':   aValue = rankOrMax(a.countyRank);   bValue = rankOrMax(b.countyRank);   break;
      case 'msaRank':      aValue = rankOrMax(a.msaRank);      bValue = rankOrMax(b.msaRank);      break;
      case 'miles':        aValue = rankOrMax(a.miles);        bValue = rankOrMax(b.miles);        break;
      case 'minutes':      aValue = rankOrMax(a.minutes);      bValue = rankOrMax(b.minutes);      break;
      // GRO-865: the API sends 0 sqft for "none on file" and the cell reads N/A, so
      // rankOrMax puts those rows at the bottom with the other missing values.
      case 'sqft':         aValue = rankOrMax(a.sqft);         bValue = rankOrMax(b.sqft);         break;
      // GRO-865: the subject store's own row (dash — no distance from itself) and rows
      // the API has no distance for (N/A) both resolve to null and sort last either way.
      case 'distanceMiles': aValue = this.distanceOrMax(a);    bValue = this.distanceOrMax(b);     break;
      default: return 0;
    }

    // Keep N/A at bottom regardless of sort direction
    if (aValue === Infinity && bValue === Infinity) return 0;
    if (aValue === Infinity) return 1;
    if (bValue === Infinity) return -1;
    return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  };

  /**
   * GRO-865: alphabetical order on the display name ("Conventional Supermarket",
   * "Supercenter", …) rather than the raw API key, so the column sorts the way it
   * reads on screen. Rows with no fit on file show N/A and stay at the bottom in
   * both directions, matching how the numeric columns treat missing values.
   */
  private compareFitCategory(a: RankingTableData, b: RankingTableData): number {
    const aLabel = this.getFitDisplay(a.fitType);
    const bLabel = this.getFitDisplay(b.fitType);

    const aMissing = aLabel === 'N/A';
    const bMissing = bLabel === 'N/A';
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;

    const comparison = aLabel.localeCompare(bLabel, 'en', { sensitivity: 'base' });
    return this.sortDirection === 'asc' ? comparison : -comparison;
  }

  /**
   * GRO-865: distance for sorting. Both missing cases — the subject store's dash and
   * an absent API value — collapse to Infinity so the shared "Infinity sorts last"
   * handling in compareRankingRows keeps them at the bottom in either direction.
   */
  private distanceOrMax(item: RankingTableData): number {
    const miles = this.getDistanceMiles(item);
    return miles == null ? Infinity : miles;
  }

  /** Sort only the current page (server-side pagination). */
  private applySortingOnCurrentPage(): void {
    this.tableData.sort(this.compareRankingRows);
    this.allTableData = [...this.tableData];
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const scrollableContainer = document.querySelector('.application-container');
      if (scrollableContainer) {
        scrollableContainer.scrollTop = 0;
      } else {
        window.scrollTo(0, 0);
      }
    }, 100);
  }

  private subscribeToDetailState(): void {
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .subscribe((state: DetailState) => {
          const wasStoreAbsent = !this.store;
          this.store = state.store;

          // When rendered directly inside the store-detail-page template (not via
          // wizard), onLoad() is never called. Trigger the data fetch the first time
          // the store becomes available so the table populates automatically.
          if (state.store && wasStoreAbsent && !this.wizard) {
            this.loadComprehensiveRankingData();
          }
          if (state.store) {
            // Status: use definitionService to look up displayName from cache
            // (same approach as StoreCriticalInsightsPanelComponent.initStatusCard)
            const currentStatus = state.store.getCurrentStatus();
            if (currentStatus) {
              this.definitionService.findOneStoreStatusType(currentStatus.getStatusSystemName())
                .pipe(take(1))
                .subscribe(def => {
                  this.storeStatusDisplay = def?.displayName || 'N/A';
                });
            } else {
              this.storeStatusDisplay = 'Unknown';
            }

            // FIT: use definitionService to look up displayName from cache
            if (state.store.fit) {
              this.definitionService.findOneFit(state.store.fit as FitType)
                .pipe(take(1))
                .subscribe(def => {
                  this.fitCategoryDisplay = def?.displayName || String(state.store.fit);
                });
            }
          }
        })
    );

    this.addSubscription(
      selectvolumeDetailState(this.ngrxStore)
        .subscribe((volume: StoreVolume) => {
          if (volume && volume.total) {
            this.estSalesVolume = `$${formatAsCurrency(volume.total, false)}`;
            this.estSalesSqft   = volume.salesSqft
              ? `$${formatAsCurrency(volume.salesSqft)} /sqft`
              : 'Not Purchased';
          } else if (volume && !volume.total) {
            this.estSalesVolume = 'Not Purchased';
            this.estSalesSqft   = 'Not Purchased';
          } else {
            this.estSalesVolume = 'Not Available';
            this.estSalesSqft   = 'Not Available';
          }
        })
    );
  }

  // Filter dropdown methods
  toggleFilterLocationDropdown(event: Event): void {
    event.stopPropagation();
    this.filterLocationDropdownOpen = !this.filterLocationDropdownOpen;
    this.closeOtherDropdowns('filterLocation');
  }

  toggleExportDropdown(event: Event): void {
    event.stopPropagation();
    this.exportDropdownOpen = !this.exportDropdownOpen;
    this.closeOtherDropdowns('export');
  }

  togglePaginationDropdown(event: Event): void {
    event.stopPropagation();
    this.paginationDropdownOpen = !this.paginationDropdownOpen;
    this.closeOtherDropdowns('pagination');
  }

  toggleMilesDropdown(event: Event): void {
    event.stopPropagation();
    this.milesDropdownOpen = !this.milesDropdownOpen;
    this.closeOtherDropdowns('miles');
    if (this.milesDropdownOpen) {
      this.positionDropdown(event.target as HTMLElement, 'miles');
    }
  }

  toggleMinutesDropdown(event: Event): void {
    event.stopPropagation();
    this.minutesDropdownOpen = !this.minutesDropdownOpen;
    this.closeOtherDropdowns('minutes');
    if (this.minutesDropdownOpen) {
      this.positionDropdown(event.target as HTMLElement, 'minutes');
    }
  }

  private positionDropdown(element: HTMLElement, type: 'miles' | 'minutes'): void {
    setTimeout(() => {
      const triggerEl = element.closest(`.${type}-filter`) as HTMLElement;
      if (!triggerEl) return;

      const rect = triggerEl.getBoundingClientRect();
      const dropdown = document.querySelector(`.${type}-filter-dropdown .filter-dropdown-options`) as HTMLElement;

      if (dropdown) {
        dropdown.style.top = `${rect.bottom + 4}px`;
        dropdown.style.left = `${rect.left}px`;
        dropdown.style.minWidth = `${Math.max(rect.width, 70)}px`;
      }
    }, 0);
  }

  async selectFilterLocationOption(option: string): Promise<void> {
    if (option === 'All Locations') {
      try {
        const wizard = new SalesVolumeRangeFilterWizard();
        wizard.model = { filterData: this.appliedFilterData, applied: false };
        await this.wizardRunner.run(wizard).afterClosed().toPromise();
        if (wizard.model?.applied) {
          this.appliedFilterData = wizard.model.filterData;
          this.currentPage = 0;
          this.loadComprehensiveRankingData();
        }
      } catch (error) {
        console.error('Error running sales volume filter wizard:', error);
      }
    } else {
      this.selectedFilterLocation = option;
      this.filterLocationDropdownOpen = false;
    }
  }

  selectExportOption(option: string): void {
    this.selectedExportOption = option;
    this.exportDropdownOpen = false;
    const metadata = {
      storeId: this.store.uuid,
      storeName: this.store.name,
      exportFormat: option
    };
        console.log("🚀 ~ ComprehensiveRankingTablePageComponent ~ selectExportOption ~ metadata:", metadata)
        this.handleExport(option);

    this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
        metadata,
        USER_TRACKING_ACTIONS.RBS_RANKING_DATA_EXPORTED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_RANKING_DATA_EXPORTED],
        true
      ).subscribe({ error: () => {} });

    // Handle export logic here
  }

  selectPaginationOption(option: string): void {
    this.selectedPaginationOption = option;
    this.pageSize = parseInt(option, 10);
    this.paginationDropdownOpen = false;
    this.currentPage = 0;
    this.loadComprehensiveRankingData();
  }

  selectMilesOption(option: string): void {
    this.selectedMilesOption = option;

    const metadata = {
      storeId: this.store.uuid,
      storeName: this.store.name,
      metricType: 'Miles',
      previousView: this.previousselectedMilesOption,
      currentView: option,
    };
    this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
        metadata,
        USER_TRACKING_ACTIONS.RBS_RANKING_MILES_CHANGED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_RANKING_MILES_CHANGED],
        true
      ).subscribe();
    this.previousselectedMilesOption = option;
    this.milesDropdownOpen = false;
    this.currentPage = 0;
    this.loadComprehensiveRankingData();
  }

  selectMinutesOption(option: string): void {
    this.selectedMinutesOption = option;
    this.minutesDropdownOpen = false;

    const metadata = {
      storeId: this.store.uuid,
      storeName: this.store.name,
      metricType: 'Minutes',
      previousView: this.previousselectedMinutesOption,
      currentView: option,
    };
    this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
        metadata,
        USER_TRACKING_ACTIONS.RBS_RANKING_MINUTES_CHANGED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_RANKING_MINUTES_CHANGED],
        true
      ).subscribe();
    this.previousselectedMinutesOption = option;
    this.currentPage = 0;
    this.loadComprehensiveRankingData();
  }

  private closeOtherDropdowns(except: string): void {
    if (except !== 'filterLocation') this.filterLocationDropdownOpen = false;
    if (except !== 'export') this.exportDropdownOpen = false;
    if (except !== 'pagination') this.paginationDropdownOpen = false;
    if (except !== 'miles') this.milesDropdownOpen = false;
    if (except !== 'minutes') this.minutesDropdownOpen = false;
  }

  // Toggle methods
  toggleSalesView(isAnnual: boolean): void {
    const previousView = this.showAsAnnualSales ? 'Annual Sales' : 'Weekly Sales';
    this.showAsAnnualSales = isAnnual;
    const metadata = {
      storeId: this.store.uuid,
      storeName: this.store.name,
      previousView,
      currentView: isAnnual ? 'Annual Sales' : 'Weekly Sales'
    };
    this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
        metadata,
        USER_TRACKING_ACTIONS.RBS_SALES_VIEW_CHANGED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_SALES_VIEW_CHANGED],
        true
      ).subscribe();
    this.currentPage = 0;
    this.loadComprehensiveRankingData();
  }

  get bannerLogoUrl(): string | null {
    const filename = this.store?.banner?.markerCloudinaryFilename;
    if (!filename) return null;
    return LogoUtil.buildLogoUrl(filename).replace('h_20,w_100', 'h_40,w_200');
  }

  get hasExportData(): boolean {
    return this.allTableData.length > 0;
  }

  getSalesColumnTitle(): string {
    return this.showAsAnnualSales ? 'Annual Sales' : 'Weekly Sales';
  }

  toggleSelectedStoreAlwaysOnTop(): void {
    this.selectedStoreAlwaysOnTop = !this.selectedStoreAlwaysOnTop;
    if (!this.selectedStoreAlwaysOnTop) {
      const metadata = {
        storeId: this.store.uuid,
        storeName: this.store.name,
        previousState: 'Checked',
        currentState: 'Unchecked'
      };
      this.hitTrackingService.hitTheUserTracking(
          USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
          metadata,
          USER_TRACKING_ACTIONS.RBS_ONTOP_TOGGLE,
          USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_ONTOP_TOGGLE],
          true
        ).subscribe();
    }

    // GRO-865: for the server-sorted columns the pin is applied before pagination, so which rows land
    // on this page depends on it — re-sorting the page in place would leave the subject store missing.
    // Re-fetch from page 1 instead, exactly as changing the sort column does.
    if (this.isServerSortedColumn()) {
      this.currentPage = 0;
      this.loadComprehensiveRankingData();
      return;
    }

    this.applySortingOnCurrentPage();
  }

  /**
   * GRO-865: columns the server orders across the full result set, rather than AWS ordering one page.
   *
   * Kept in step with FULL_SET_SORT_FIELDS in RankByStoreComprehensiveRankingService — these are the
   * sort keys whose ordering happens after pagination decisions, so anything affecting the order needs
   * a re-fetch rather than an in-place page sort.
   */
  private isServerSortedColumn(): boolean {
    return ['fitType', 'sqft', 'distanceMiles', 'miles', 'minutes'].includes(this.sortColumn);
  }

  // Export handling
  private handleExport(option: string): void {
    if (this.isExporting) return; // ignore rapid re-clicks while a fetch is in flight

    this.isExporting = true;
    // Fetch the full ranking result set (all pages) in a single request so the export
    // contains every row, not just the visible page. On-screen table state is untouched.
    this.addSubscription(
      this.fetchAllRankingRowsForExport()
        .pipe(finalize(() => (this.isExporting = false)))
        .subscribe((rows) => {
          if (option === 'Export to PDF') {
            this.exportToPdf(rows);
          } else if (option === 'Export to CSV') {
            this.exportToCsv(rows);
          } else if (option === 'Export to Excel') {
            this.exportToExcel(rows);
          }
        })
    );
  }

  /** Server caps page size, so exports fetch the result set in chunks of this many rows. */
  private static readonly EXPORT_CHUNK_SIZE = 1000;

  /** Pulls the row array out of any supported response shape (Spring Page / custom / bare). */
  private extractResponseItems(response: any): any[] {
    return Array.isArray(response?.content) ? response.content :
           Array.isArray(response?.data)    ? response.data    :
           Array.isArray(response)          ? response         : [];
  }

  /** Reads the server-reported total row count from any supported response shape. */
  private extractResponseTotalCount(response: any): number | null {
    return response?.pagination?.total_count ??
           response?.totalElements ?? response?.total_elements ?? response?.total ?? null;
  }

  /**
   * Requests every row in the current ranking result set for export, using the active
   * filters and sort. The server caps page size, so this fetches the first chunk to learn
   * the total, then fetches any remaining chunks in parallel and appends them in page order.
   * Rows are mapped and ordered exactly like the on-screen table, but table/pagination state
   * is never mutated — export-only. On error, falls back to the current page so export still works.
   */
  private fetchAllRankingRowsForExport(): Observable<RankingTableData[]> {
    const chunkSize = ComprehensiveRankingTablePageComponent.EXPORT_CHUNK_SIZE;
    const payload   = this.buildApiPayload();
    const sort      = this.buildSortParam();

    const fetchPage = (page: number): Observable<any[]> =>
      this.storeRankingService
        .getComprehensiveRanking(payload, page, chunkSize, sort)
        .pipe(map((response: any) => this.extractResponseItems(response)));

    return this.storeRankingService
      .getComprehensiveRanking(payload, 0, chunkSize, sort)
      .pipe(
        switchMap((firstResponse: any) => {
          const firstItems = this.extractResponseItems(firstResponse);
          const total      = this.extractResponseTotalCount(firstResponse) ?? firstItems.length;
          const totalPages = Math.max(1, Math.ceil(total / chunkSize));

          if (totalPages <= 1) {
            return of(firstItems);
          }

          // Fetch the remaining pages in parallel; forkJoin preserves array order,
          // so concatenating keeps the rows in correct page sequence.
          const remaining: Observable<any[]>[] = [];
          for (let page = 1; page < totalPages; page++) {
            remaining.push(fetchPage(page));
          }
          return forkJoin(remaining).pipe(
            map((pages: any[][]) => firstItems.concat(...pages))
          );
        }),
        map((items: any[]) =>
          this.dedupeByStoreUuid(items.map((item: any) => this.mapResponseItemToTableData(item)))
            .sort(this.compareRankingRows)
        ),
        catchError((error) => {
          console.error('Error fetching full ranking data for export:', error);
          return of([...this.allTableData]); // fall back to current page
        })
      );
  }

  /**
   * The ranking response carries the subject store twice — pinned at the top of the
   * result set, and again at its natural rank position. Paging hides that on screen
   * (the two copies land on different pages), but an export pulls the whole set and
   * compareRankingRows sorts every selected row to the top, so the copies end up side
   * by side. Keep the first occurrence of each store; rows without a UUID are left
   * alone rather than collapsed into one.
   */
  private dedupeByStoreUuid(rows: RankingTableData[]): RankingTableData[] {
    const seen = new Set<string>();
    return rows.filter((row) => {
      if (!row.storeUuid) {
        return true;
      }
      if (seen.has(row.storeUuid)) {
        return false;
      }
      seen.add(row.storeUuid);
      return true;
    });
  }

  private async exportToPdf(rows: RankingTableData[]): Promise<void> {
    const d = this.appliedFilterData;

    // Build filter display strings
    const formatSalesVolume = (range: { minValue: number; maxValue: number } | null): string => {
      if (!range) return 'Not Applied';
      const fmt = (v: number) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${Math.round(v / 1000)}K`;
      return `${fmt(range.minValue)} – ${fmt(range.maxValue)}`;
    };
    const joinOrNot = (arr: string[]): string => arr?.length ? arr.join(', ') : 'Not Applied';

    const filtersData = d ? {
      salesVolume: formatSalesVolume(d.salesVolume?.range ?? null),
      states: (() => {
        const parts: string[] = [];
        if (d.geography?.states?.length) parts.push(...(d.geography.stateLabels?.length ? d.geography.stateLabels : d.geography.states));
        if (d.geography?.derivedStates?.length) parts.push(...(d.geography.derivedStateLabels?.length ? d.geography.derivedStateLabels : d.geography.derivedStates));
        if (d.geography?.counties?.length) parts.push(...d.geography.counties);
        if (d.geography?.zipCodes?.length) parts.push(...d.geography.zipCodes);
        return parts.length ? parts.join(', ') : 'Not Applied';
      })(),
      cbsa: joinOrNot(d.geography?.msaLabels?.length ? d.geography.msaLabels : d.geography.msas),
      fit: joinOrNot(d.company?.bannerLabels?.length ? d.company.bannerLabels : d.company?.banners),
      sqft: d.sqft?.range
        ? `${d.sqft.range.minValue.toLocaleString()} – ${d.sqft.range.maxValue.toLocaleString()} sqft`
        : 'Not Applied',
    } : undefined;

    // Build store address from location model
    const loc = this.store?.space?.location;
    const storeAddress = loc
      ? [loc.addressLine1, loc.city, loc.state, loc.postalCode].filter(Boolean).join(', ')
      : (this.store?.name || 'N/A');

    // Build table rows from the full export result set (no client-side row cap)
    const fmtRank = (rank: number, total?: number): string => {
      if (!rank || rank === 0) return 'N/A';
      return total ? `${rank}/${total}` : String(rank);
    };
    const tableRows: RankingTablePdfRow[] = rows.map(row => ({
      name: row.name,
      storeNumber: row.storeNumber,
      address: row.address,
      annualSales: row.annualSales,
      nationalRank: fmtRank(row.nationalRank, row.nationalRankTotal),
      stateRank:    fmtRank(row.stateRank,    row.stateRankTotal),
      countyRank:   fmtRank(row.countyRank,   row.countyRankTotal),
      msaRank:      fmtRank(row.msaRank,      row.msaRankTotal),
      miles:        fmtRank(row.miles,         row.milesTotal),
      minutes:      fmtRank(row.minutes,       row.minutesTotal),
      fit:          this.getFitDisplay(row.fitType),
      storeSize:    row.sqft ? Number(row.sqft).toLocaleString() : 'N/A',
      distance:     this.getDistanceDisplay(row),
      isSelected: row.isSelected,
    }));

    await this.dynamicPdfService.generateRankingTablePdf(
      {
        storeName: this.store?.name || 'Store',
        storeAddress,
        storeStatus:    this.storeStatusDisplay,
        estSalesVolume: this.estSalesVolume,
        estSalesSqft:   this.estSalesSqft,
        fitCategory:    this.getFitCategory(),
        milesOption:    this.selectedMilesOption,
        minutesOption:  this.selectedMinutesOption,
        salesType:      this.showAsAnnualSales ? 'Annual Sales' : 'Weekly Sales',
        storeSizeLabel: this.storeSizeLabel,
        filters: filtersData,
        tableRows,
      },
      `store-ranking-${Date.now()}.pdf`
    );
  }

  private getStoreAddress(): string {
    if (!this.store) return 'N/A';
    const loc = this.store.space?.location;
    if (loc) {
      const parts = [loc.addressLine1, loc.city].filter(p => !!p);
      return parts.join(', ') || (this.store as any).address || 'N/A';
    }
    return (this.store as any).address || 'N/A';
  }


  private getFitCategory(): string {
    return this.fitCategoryDisplay;
  }

  /**
   * @param numeric true for Excel, which keeps Store Size / Distance as real numbers and
   *                applies its own display format; false for CSV, where the text in the
   *                file is all the reader gets.
   */
  private buildExportRows(rows: RankingTableData[], numeric = false): any[][] {
    const d = this.appliedFilterData;
    const now = new Date();
    const generated = `${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}-${now.getFullYear()}`;

    const formatSalesVolume = (range: { minValue: number; maxValue: number } | null): string => {
      if (!range) return 'Not Applied';
      const fmt = (v: number) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${Math.round(v / 1000)}K`;
      return `${fmt(range.minValue)} - ${fmt(range.maxValue)}`;
    };
    const joinOrNot = (arr: string[]): string => arr?.length ? arr.join(', ') : 'Not Applied';

    const salesVolumeFilter = d ? formatSalesVolume(d.salesVolume?.range ?? null) : 'Not Applied';
    const stateNames = [
      ...(d?.geography?.states?.length ? (d.geography.stateLabels?.length ? d.geography.stateLabels : d.geography.states) : []),
      ...(d?.geography?.derivedStates?.length ? (d.geography.derivedStateLabels?.length ? d.geography.derivedStateLabels : d.geography.derivedStates) : []),
    ];
    const statesFilter = stateNames.length ? stateNames.join(', ') : 'Not Applied';
    const cbsaFilter = d?.geography?.msas?.length ? (d.geography.msaLabels?.length ? d.geography.msaLabels : d.geography.msas).join(', ') : 'Not Applied';
    const fitFilter = d ? joinOrNot(d.company?.bannerLabels?.length ? d.company.bannerLabels : d.company?.banners) : 'Not Applied';
    const sqftFilter = d?.sqft?.range
      ? `${d.sqft.range.minValue.toLocaleString()} - ${d.sqft.range.maxValue.toLocaleString()}`
      : 'Not Applied';

    const milesLabel = `${this.selectedMilesOption} Miles`;
    const minutesLabel = `${this.selectedMinutesOption} Minutes`;

    const exportRows: any[][] = [
      ['STORE INFORMATION'],
      ['Store Address', this.getStoreAddress()],
      ['Status', this.storeStatusDisplay],
      ['Est. Sales Volume', this.estSalesVolume],
      ['Est. Sales/SQFT', this.estSalesSqft],
      ['FIT Category', this.getFitCategory()],
      ['Generated', generated],
      [],
      ['FILTERS APPLIED'],
      ['Sales Volume', salesVolumeFilter],
      ['States', statesFilter],
      ['CBSA', cbsaFilter],
      ['FIT', fitFilter],
      ['SQFT', sqftFilter],
      [],
      ['STORE SALES AND RANKINGS SUMMARY'],
      [
        // FIT / Sq Ft are no longer here — GRO-711 moved them to the end of the row as
        // 'Store FIT Category' / 'Store Size' so the export column order matches the
        // on-screen table and the PDF.
        'Store Name', 'Store Number', 'Address', 'Type', 'Status',
        this.getSalesColumnTitle(),
        'NATL Rank', 'NATL Total', 'NATL Band',
        'State Rank', 'State Total', 'State Band',
        'County Rank', 'County Total', 'County Band',
        'MSA/CBSA Rank', 'MSA/CBSA Total', 'MSA Band',
        `${milesLabel} Rank`, `${milesLabel} Total`,
        `${minutesLabel} Rank`, `${minutesLabel} Total`,
        // GRO-711 columns — appended last, matching the on-screen table and the PDF
        'Store FIT Category', `Store Size (${this.storeSizeLabel})`, 'Distance (Miles)',
      ],
      ...rows.map(row => {
        const rankVal = (r: number) => (!r || r === 0) ? 'N/A' : r;
        return [
          row.isSelected ? `${row.name} (Selected Store)` : row.name,
          row.storeNumber ?? '',
          row.address,
          row.storeType === 'selected' ? 'Selected Store' : row.storeType === 'banner' ? 'Same Banner' : 'Competitor',
          row.status,
          row.annualSales,
          rankVal(row.nationalRank), row.nationalRankTotal ?? '', row.industryNationalBand ?? '',
          rankVal(row.stateRank),    row.stateRankTotal    ?? '', row.industryStateBand    ?? '',
          rankVal(row.countyRank),   row.countyRankTotal   ?? '', row.industryCountyBand   ?? '',
          rankVal(row.msaRank),      row.msaRankTotal      ?? '', row.industryMsaBand      ?? '',
          rankVal(row.miles),        row.milesTotal        ?? '',
          rankVal(row.minutes),      row.minutesTotal      ?? '',
          this.getFitDisplay(row.fitType),
          this.getStoreSizeExportValue(row, numeric),
          this.getDistanceExportValue(row, numeric),
        ];
      })
    ];

    // Pad the metadata/spacer rows out to the table's column count so every CSV line
    // carries the same number of fields as the ranking header row.
    const columnCount = exportRows.reduce((widest, row) => Math.max(widest, row.length), 0);
    return exportRows.map(row =>
      row.length === columnCount
        ? row
        : [...row, ...new Array(columnCount - row.length).fill('')]
    );
  }

  private exportToCsv(rows: RankingTableData[]): void {
    const data = this.buildExportRows(rows);
    const csvContent = data.map(row =>
      row.map(cell => {
        const str = cell == null ? '' : String(cell);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(',')
    ).join('\r\n');

    // Lead with a UTF-8 BOM so Excel decodes the file as UTF-8 rather than the system
    // ANSI codepage — without it any non-ASCII character (accented store names, the
    // em dash fallbacks) arrives as mojibake.
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const storeName = (this.store?.name || 'store').replace(/[^a-zA-Z0-9]/g, '_');
    this.fileDownloadService.downloadBlob(blob, 'text/csv;charset=utf-8', `store-ranking-${storeName}-${Date.now()}.csv`);
  }

  private async exportToExcel(rows: RankingTableData[]): Promise<void> {
    // Excel keeps Store Size / Distance numeric and formats them via numFmt below,
    // so the columns display as "25,000" / "1,450.7" but still sort and total correctly.
    const data = this.buildExportRows(rows, true);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Store Rankings');

    // One width per column of the ranking header row, in header order:
    // Store Name, Store Number, Address … Minutes Total, then the three GRO-711 columns.
    // Store Size is wide enough for its full header, "Store Size (Sales Area SQFT)" (GRO-911).
    worksheet.columns = [
      30, 14, 45, 15, 14, 14,
      12, 12, 12, 12, 12, 12, 12, 12, 12, 14, 14, 12,
      14, 14, 16, 16,
      20, 30, 14,
    ].map(width => ({ width }));

    // Columns 24/25 are Store Size and Distance (1-based, last two of the header row).
    // Distance uses '#' placeholders after the point rather than a fixed '0.0' so Excel
    // shows however many decimals the value actually has instead of rounding to one.
    worksheet.getColumn(24).numFmt = '#,##0';
    worksheet.getColumn(25).numFmt = '#,##0.##########';

    data.forEach(row => worksheet.addRow(row));

    const buffer = await workbook.xlsx.writeBuffer();
    const storeName = (this.store?.name || 'store').replace(/[^a-zA-Z0-9]/g, '_');
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `store-ranking-${storeName}-${Date.now()}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  // Row hover handler
  onRowHover(item: RankingTableData, isHovered: boolean): void {
    item.isHovered = isHovered;
  }

  // Get formatted rank display
  // GRO-721: always show `rank / total` for every row (no longer hover/selected-only)
  getRankDisplay(rank: number, total: number | undefined, _isHovered: boolean = false, _isSelected: boolean = false): string {
    if (!rank || rank === 0) return 'N/A';
    return total ? `${rank}/${total}` : `${rank}`;
  }

  // Helper methods for styling
  /**
   * Returns CSS class for the ranking badge.
   * Uses the API-provided band value as primary signal (accurate + free of
   * threshold guessing). Falls back to percentile calculation when band is absent.
   *
   * Band mapping (API → class):
   *   TOP          → ranking-badge-excellent  (dark blue, #1)
   *   GOOD         → ranking-badge-good       (green)
   *   AVERAGE      → ranking-badge-average    (yellow/amber)
   *   BELOW_AVERAGE→ ranking-badge-warning    (orange)
   *   POOR         → ranking-badge-poor       (red)
   */
  /**
   * 6-band system (matches backend Percentile bands):
   *   TOP           (0–1 %)   → teal    ranking-badge-top
   *   EXCELLENT     (1–10%)   → green   ranking-badge-excellent
   *   GOOD          (10–40%)  → blue    ranking-badge-good
   *   AVG / AVERAGE (40–70%)  → yellow  ranking-badge-average
   *   BELOW / BELOW_AVERAGE / POOR (70–100%) → red ranking-badge-poor
   *   N/A                     → grey   ranking-badge-no-data
   */
  getRankingBadgeClass(rank: number, total: number | undefined, band?: BandValue): string {
    // ── Band-based (preferred when API provides it) ─────────────────────────
    if (band) {
      switch (band.toUpperCase()) {
        case 'TOP':           return 'ranking-badge-top';       // Teal
        case 'EXCELLENT':     return 'ranking-badge-excellent';  // Green
        case 'GOOD':          return 'ranking-badge-good';       // Blue
        case 'AVG':
        case 'AVERAGE':       return 'ranking-badge-average';    // Yellow
        case 'BELOW':
        case 'BELOW_AVERAGE':
        case 'POOR':          return 'ranking-badge-poor';       // Red
        case 'N/A':           return 'ranking-badge-no-data';    // Grey
      }
    }

    // ── No data ─────────────────────────────────────────────────────────────
    if (rank == null || rank === 0) return 'ranking-badge-no-data';

    // ── Percentile fallback (banner ranks have no band from API) ────────────
    if (total && total > 0) {
      const pct = (rank / total) * 100;
      if (pct <= 1)  return 'ranking-badge-top';       // Teal:   top 1%
      if (pct <= 10) return 'ranking-badge-excellent';  // Green:  top 1–10%
      if (pct <= 40) return 'ranking-badge-good';       // Blue:   top 10–40%
      if (pct <= 70) return 'ranking-badge-average';    // Yellow: top 40–70%
      return 'ranking-badge-poor';                       // Red:    bottom 30%
    }

    // ── Absolute-rank fallback (no total available) ──────────────────────────
    if (rank <= 1)  return 'ranking-badge-top';
    if (rank <= 5)  return 'ranking-badge-excellent';
    if (rank <= 15) return 'ranking-badge-good';
    if (rank <= 30) return 'ranking-badge-average';
    return 'ranking-badge-poor';
  }


  async openRankDetailModal(item: RankingTableData, rankingType: string): Promise<void> {
    const metadata = {
      storeId: this.store.uuid,
      storeName: this.store.name,
      rankType: rankingType,
      storeRank: this.getStoreRankByType(item, rankingType),
    };

    this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
        metadata,
        USER_TRACKING_ACTIONS.RBS_EXPAND_ICON_CLICK,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_EXPAND_ICON_CLICK],
        true
      ).subscribe({ error: () => {} });
    try {
      const wizard = new RankByStoreWizard();
      const type = rankingType.toLowerCase();

      const needsGeoFetch =
        (type === 'state' && !!item.stateKey) ||
        (type === 'county' && !!item.countyKey) ||
        ((type === 'msa' || type === 'cbsa') && !!item.cbsaKey) ||
        type === 'miles' ||
        type === 'minutes';

      wizard.model = {
        rankingType,
        tableData: [],
        salesLabel: this.getSalesColumnTitle(),
        selectedMilesOption: this.selectedMilesOption,
        selectedMinutesOption: this.selectedMinutesOption,
        clickedStoreUuid: item.storeUuid,
        // Pass a promise so the popup opens immediately and loads data in background
        dataPromise: needsGeoFetch
          ? this.fetchAllGeoFilteredData(item, type)
          : Promise.resolve(this.allTableData),
      };

      // Open popup right away — data loads inside the wizard
      await this.wizardRunner.run(wizard).afterClosed().toPromise();
    } catch (error) {
      console.error('Error opening rank detail modal:', error);
    }
  }

  /**
   * Fetches ALL pages of the comprehensive ranking API for a specific geo scope
   * (state / county / cbsa) using page size 1000, running extra pages in parallel.
   * Miles/Minutes: uses the drive_miles endpoint which returns a plain array (no pagination).
   * County: backend has no county filter — fetch by state then filter client-side.
   */
  private async fetchAllGeoFilteredData(item: RankingTableData, type: string): Promise<RankingTableData[]> {
    const payload = this.buildApiPayload();
    if (!payload.filter) {
      payload.filter = {};
    }

    // ── Miles / Minutes → drive_miles endpoint (returns plain array, camelCase) ──
    if (type === 'miles' || type === 'minutes') {
      if (type === 'miles') {
        delete payload.driveTime;
      } else {
        delete payload.miles;
      }
      const response = await this.storeRankingService
        .getDriveMilesRanking(payload)
        .pipe(take(1))
        .toPromise();
      const items: any[] = Array.isArray(response) ? response : [];
      return items.map((raw: any) => this.mapDriveMilesItemToTableData(raw));
    }

    // ── Geo-filtered (state / county / cbsa) → comprehensive endpoint with pagination ──
    const PAGE_SIZE = 1000;

    if (type === 'state' && item.stateKey) {
      payload.filter.states = [item.stateKey];
    } else if (type === 'county') {
      if (item.stateKey) {
        payload.filter.states = [item.stateKey];
      }
    } else if ((type === 'msa' || type === 'cbsa') && item.cbsaKey) {
      payload.filter.cbsas = [item.cbsaKey];
      if (item.stateKey) {
        payload.filter.states = [item.stateKey];
      }
    }

    const firstResponse = await this.storeRankingService
      .getComprehensiveRanking(payload, 0, PAGE_SIZE)
      .pipe(take(1))
      .toPromise();

    const extractItems = (res: any): any[] =>
      res?.data ?? res?.content ?? (Array.isArray(res) ? res : []);

    const firstItems = extractItems(firstResponse);

    const totalCount: number =
      firstResponse?.pagination?.total_count ??
      firstResponse?.totalElements ??
      firstResponse?.total ??
      firstItems.length;

    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    let allRawItems = [...firstItems];

    if (totalPages > 1) {
      const pageRequests = [];
      for (let page = 1; page < totalPages; page++) {
        pageRequests.push(
          this.storeRankingService.getComprehensiveRanking(payload, page, PAGE_SIZE).pipe(take(1))
        );
      }
      const remainingResponses = await forkJoin(pageRequests).toPromise();
      if (remainingResponses) {
        for (const res of remainingResponses) {
          allRawItems = allRawItems.concat(extractItems(res));
        }
      }
    }

    const mapped = allRawItems.map((raw: any) => this.mapResponseItemToTableData(raw));

    if (type === 'county' && item.countyKey) {
      return mapped.filter(r => r.countyKey === item.countyKey);
    }

    return mapped;
  }

  /** Maps one drive_miles API row (camelCase) to RankingTableData. */
  private mapDriveMilesItemToTableData(item: any): RankingTableData {
    const isSelected = !!item.storeUuid && item.storeUuid === this.store?.uuid;
    const isBanner   = !isSelected && item.bannerId === this.store?.banner?.id;

    const address = [item.addressLine1, item.state, item.zipCode]
      .filter(Boolean).join(', ');

    const volume = item.storeVolume;
    const annualSales = volume != null ? this.formatVolume(volume) : 'N/A';

    return {
      name:       item.storeName   ?? 'Unknown',
      address,
      storeUuid:  item.storeUuid   ?? undefined,
      bannerName: item.bannerName  ?? undefined,
      storeType:  isSelected ? 'selected' : (isBanner ? 'banner' : 'other'),
      annualSales,
      fitType: item.storeFitType ?? '—',
      status:  item.currentStatus ?? '—',
      sqft:    item.squareFootage ?? 0,
      distanceMiles: item.distanceMiles ?? null,

      nationalRank:      item.rankBanner      ?? 0,
      nationalRankTotal: item.totalCountBanner ?? undefined,
      nationalBand:      item.rankBandBanner   ?? undefined,

      stateRank:    0,
      countyRank:   0,
      msaRank:      0,

      // Banner (company) ranks for miles & minutes
      miles:        item.rankBanner      ?? 0,
      milesTotal:   item.totalCountBanner ?? undefined,
      milesBand:    item.rankBandBanner   ?? undefined,
      minutes:      item.rankBanner      ?? 0,
      minutesTotal: item.totalCountBanner ?? undefined,
      minutesBand:  item.rankBandBanner   ?? undefined,

      // Industry ranks for miles & minutes
      industryNationalRank:      item.rankIndustry     ?? undefined,
      industryNationalRankTotal: item.totalCount        ?? undefined,
      industryNationalBand:      item.rankBandIndustry  ?? undefined,
      industryMiles:             item.rankIndustry      ?? undefined,
      industryMilesTotal:        item.totalCount        ?? undefined,
      industryMilesBand:         item.rankBandIndustry  ?? undefined,
      industryMinutes:           item.rankIndustry      ?? undefined,
      industryMinutesTotal:      item.totalCount        ?? undefined,
      industryMinutesBand:       item.rankBandIndustry  ?? undefined,

      stateKey:  item.state   ?? undefined,
      countyKey: item.county  ?? undefined,
      cbsaKey:   item.cbsa    ?? undefined,

      salesSqftByTotalArea: item.salesSqftByTotalArea ?? undefined,
      salesSqftBySaleArea:  item.salesSqftBySaleArea  ?? undefined,
    } as RankingTableData;
  }

  /**
   * Get the rank value for a specific ranking type from the clicked store item
   */
  private getStoreRankByType(item: RankingTableData, rankingType: string): number {
    console.log('Getting rank for type:', rankingType, 'Item rank values:', {
      nationalRank: item.nationalRank,
      stateRank: item.stateRank,
      countyRank: item.countyRank,
      msaRank: item.msaRank,
      miles: item.miles,
      minutes: item.minutes
    });
    
    switch (rankingType.toLowerCase()) {
      case 'national':
        return item.nationalRank || 0;
      case 'state':
        return item.stateRank || 0;
      case 'county':
        return item.countyRank || 0;
      case 'msa':
      case 'cbsa':
        return item.msaRank || 0;
      case 'miles':
        return item.miles || 0;
      case 'minutes':
        return item.minutes || 0;
      default:
        console.warn('Unknown ranking type:', rankingType);
        return 0;
    }
  }

  async openDetailReport(item: RankingTableData): Promise<void> {
    try {
      const metadata = {
        storeId: this.store.uuid,
        storeName: this.store.name,
        bannerName: item.name,
      };
      this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.RBS_COMPREHENSIVE_RANKING,
        metadata,
        USER_TRACKING_ACTIONS.RBS_DETAIL_REPORT_OPENED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_DETAIL_REPORT_OPENED],
        true
      ).subscribe();
      const wizard = new RankByStorDetailReportWizard();
      wizard.model = {
        miles:   this.selectedMilesOption,
        minutes: this.selectedMinutesOption,
        salesLabel: this.getSalesColumnTitle(),
        store: item,       // full RankingTableData — both industry + banner fields
      };
      await this.wizardRunner.run(wizard).afterClosed().toPromise();
    } catch (error) {
      console.error('Error opening detail report:', error);
    }
  }


  getStoreIconClass(storeType: string): string {
    switch (storeType) {
      case 'selected':
        return 'store-icon-selected';
      case 'banner':
        return 'store-icon-banner';
      default:
        return 'store-icon-other';
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(_event: Event): void {
    this.filterLocationDropdownOpen = false;
    this.exportDropdownOpen = false;
    this.paginationDropdownOpen = false;
    this.milesDropdownOpen = false;
    this.minutesDropdownOpen = false;
  }

  // Sales Volume Filter Event Handlers
  onSalesVolumeFilterClose(): void {
    this.showSalesVolumeFilter = false;
  }

  onSalesVolumeFilterApply(filterData: SalesVolumeRangeData): void {
    this.showSalesVolumeFilter = false;
    this.selectedFilterLocation = `Filter Location (${filterData.minValue} - ${filterData.maxValue})`;
    // Apply the filter logic here
    this.applyVolumeRangeFilter(filterData);
  }

  private applyVolumeRangeFilter(filterData: SalesVolumeRangeData): void {
    // Filter the table data based on sales volume range
    // This would typically involve calling a service to fetch filtered data
    console.log('Applying volume range filter:', filterData);

    // Example: Filter tableData by sales volume
    // const minValue = filterData.minValue;
    // const maxValue = filterData.maxValue;
    // this.tableData = this.tableData.filter(item => {
    //   const salesValue = this.parseSalesValue(item.annualSales);
    //   return salesValue >= minValue && salesValue <= maxValue;
    // });
  }

  private resetVolumeRangeFilter(): void {
    // TODO: reset table data to show all items via service
  }

  // private parseSalesValue(salesString: string): number {
  //   // Parse sales string like "$7.6M" to numeric value
  //   const value = salesString.replace(/[$,]/g, '');
  //   if (value.includes('M')) {
  //     return parseFloat(value.replace('M', '')) * 1000000;
  //   }
  //   if (value.includes('K')) {
  //     return parseFloat(value.replace('K', '')) * 1000;
  //   }
  //   return parseFloat(value);
  // }

  onSalesVolumeFilterReset(): void {
    this.selectedFilterLocation = 'Filter Location';
    this.resetVolumeRangeFilter();
  }

  // Pagination methods
  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadComprehensiveRankingData();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadComprehensiveRankingData();
    }
  }

  // Sorting methods
  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    const metadata = {
      storeId: this.store.uuid,
      storeName: this.store.name,
      columnName: column,
      sortDirection: this.sortDirection
    };
    this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.STORE_SALES_PERFORMANCE_PANEL,
        metadata,
        USER_TRACKING_ACTIONS.RBS_COLUMN_SORT_CHANGED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.RBS_COLUMN_SORT_CHANGED],
        true
      ).subscribe();
    // Reset to page 1 and re-fetch from API with new sort param
    this.currentPage = 0;
    this.loadComprehensiveRankingData();
  }

  getSortIconClass(column: string): string {
    if (this.sortColumn !== column) return 'sort-icon sort-default';
    return this.sortDirection === 'asc' ? 'sort-icon sort-asc' : 'sort-icon sort-desc';
  }

  /**
   * Builds the sort query parameter for the API call.
   * Maps UI column names → API snake_case field names and appends direction.
   * Returns undefined when no sort is active (omits the param).
   */
  private buildSortParam(): string | undefined {
    if (!this.sortColumn) return undefined;

    const fieldMap: Record<string, string> = {
      nationalRank: 'national',
      stateRank:    'state',
      countyRank:   'county',
      msaRank:      'cbsa',
      miles:        'miles',
      minutes:      'drive_time',
      // GRO-865: the three post-launch columns sort on the response fields of the
      // same name, so the server orders the whole result set rather than one page.
      fitType:       'store_fit_type',
      sqft:          'square_footage',
      distanceMiles: 'distance_miles',
    };

    const apiField = fieldMap[this.sortColumn];
    if (!apiField) return undefined;

    return `${apiField},${this.sortDirection}`;
  }

  // Filter Chips Methods
  hasAppliedFilters(): boolean {
    if (!this.appliedFilterData) return false;
    const d = this.appliedFilterData;
    const hasSalesVolume = d.salesVolume?.range &&
      (d.salesVolume.range.minValue !== 5000 || d.salesVolume.range.maxValue !== 7070000);
    const hasGeo = (d.geography?.states?.length > 0) ||
      (d.geography?.derivedStates?.length > 0) ||
      (d.geography?.counties?.length > 0) ||
      (d.geography?.msas?.length > 0) ||
      (d.geography?.zipCodes?.length > 0);
    const hasCompany = d.company?.banners?.length > 0;
    const hasSqft = d.sqft?.range != null;
    return !!(hasSalesVolume || hasGeo || hasCompany || hasSqft);
  }

  getAppliedFilterChips(): { category: FilterCategoryKey | string; label: string }[] {
    if (!this.appliedFilterData) return [];
    const chips: { category: FilterCategoryKey | string; label: string }[] = [];
    const d = this.appliedFilterData;

    // Sales Volume
    if (d.salesVolume?.range &&
        (d.salesVolume.range.minValue !== 5000 || d.salesVolume.range.maxValue !== 7070000)) {
      const min = this.formatChipValue(d.salesVolume.range.minValue);
      const max = this.formatChipValue(d.salesVolume.range.maxValue);
      chips.push({ category: 'sales-volume', label: `Sales: $${min} - $${max}` });
    }

    // Geography - States (sub-category key so only states are cleared on ×).
    // Includes derived states (unsubscribed states reached via their subscribed CBSAs).
    const subscribedStateLabels = d.geography?.states?.length ? (d.geography.stateLabels?.length ? d.geography.stateLabels : d.geography.states) : [];
    const derivedStateLabels = d.geography?.derivedStates?.length ? (d.geography.derivedStateLabels?.length ? d.geography.derivedStateLabels : d.geography.derivedStates) : [];
    const allStateLabels = [...subscribedStateLabels, ...derivedStateLabels];
    if (allStateLabels.length > 0) {
      const label = allStateLabels.length === 1
        ? `States: ${allStateLabels[0]}`
        : `States: ${allStateLabels[0]} +${allStateLabels.length - 1}`;
      chips.push({ category: 'geography-states', label });
    }

    // Geography - Counties
    if (d.geography?.counties?.length > 0) {
      const counties = d.geography.counties;
      const label = counties.length === 1
        ? `Counties: ${counties[0]}`
        : `Counties: ${counties[0]} +${counties.length - 1}`;
      chips.push({ category: 'geography-counties', label });
    }

    // Geography - MSAs
    if (d.geography?.msas?.length > 0) {
      const msaLabels = d.geography.msaLabels?.length ? d.geography.msaLabels : d.geography.msas;
      const label = msaLabels.length === 1
        ? `MSAs: ${msaLabels[0]}`
        : `MSAs: ${msaLabels[0]} +${msaLabels.length - 1}`;
      chips.push({ category: 'geography-msas', label });
    }

    // Geography - Zip Codes
    if (d.geography?.zipCodes?.length > 0) {
      const zips = d.geography.zipCodes;
      const label = zips.length === 1
        ? `Zip: ${zips[0]}`
        : `Zip: ${zips[0]} +${zips.length - 1}`;
      chips.push({ category: 'geography-zipCodes', label });
    }

    // Company/FIT
    if (d.company?.banners?.length > 0) {
      const bannerLabels = d.company.bannerLabels?.length ? d.company.bannerLabels : d.company.banners;
      const label = bannerLabels.length === 1
        ? `FIT: ${bannerLabels[0]}`
        : `FIT: ${bannerLabels[0]} +${bannerLabels.length - 1}`;
      chips.push({ category: 'company', label });
    }

    // SQFT
    if (d.sqft?.range) {
      const min = d.sqft.range.minValue.toLocaleString();
      const max = d.sqft.range.maxValue.toLocaleString();
      chips.push({ category: 'sqft', label: `SQFT: ${min} - ${max}` });
    }

    return chips;
  }

  private formatChipValue(value: number): string {
    if (value >= 1000000) {
      const millions = (value / 1000000).toFixed(1);
      return millions.endsWith('.0') ? `${Math.floor(value / 1000000)}M` : `${millions}M`;
    }
    if (value >= 1000) {
      return `${Math.floor(value / 1000)}K`;
    }
    return value.toLocaleString();
  }

  async openFilterWithCategory(category: FilterCategoryKey): Promise<void> {
    try {
      // Normalize sub-geography keys so the wizard opens on the Geography tab
      const wizardCategory: FilterCategoryKey = (category as string).startsWith('geography-')
        ? 'geography'
        : category;
      const wizard = new SalesVolumeRangeFilterWizard();
      wizard.model = { filterData: this.appliedFilterData, applied: false, startingCategory: wizardCategory };
      await this.wizardRunner.run(wizard).afterClosed().toPromise();
      if (wizard.model?.applied) {
        this.appliedFilterData = wizard.model.filterData;
        this.currentPage = 0;
        this.loadComprehensiveRankingData();
      }
    } catch (error) {
      console.error('Error running filter wizard:', error);
    }
  }

  clearFilterCategory(category: FilterCategoryKey | string): void {
    if (!this.appliedFilterData) return;
    switch (category) {
      case 'sales-volume':
        this.appliedFilterData.salesVolume = {range: null};
        break;
      // Sub-geography keys — clear only the specific sub-filter
      case 'geography-states':
        this.appliedFilterData.geography = { ...this.appliedFilterData.geography, states: [], stateLabels: [], derivedStates: [], derivedStateLabels: [], derivedCbsas: [] };
        break;
      case 'geography-counties':
        this.appliedFilterData.geography = { ...this.appliedFilterData.geography, counties: [] };
        break;
      case 'geography-msas':
        this.appliedFilterData.geography = { ...this.appliedFilterData.geography, msas: [] };
        break;
      case 'geography-zipCodes':
        this.appliedFilterData.geography = { ...this.appliedFilterData.geography, zipCodes: [] };
        break;
      case 'geography':
        // Clear all geography sub-filters at once
        this.appliedFilterData.geography = { states: [], stateLabels: [], derivedStates: [], derivedStateLabels: [], derivedCbsas: [], counties: [], msas: [], msaLabels: [], zipCodes: [], driveTime: undefined };
        break;
      case 'company':
        this.appliedFilterData.company = { banners: [] };
        // When FIT filter is cleared, adjust SQFT values to original bounds if SQFT filter is applied
        if (this.appliedFilterData.sqft?.range) {
          const currentMin = this.appliedFilterData.sqft.range.minValue;
          const currentMax = this.appliedFilterData.sqft.range.maxValue;
          
          // Use hardcoded original API bounds (should match your API response)
          const originalMin = 0.15; // Replace with actual API min value
          const originalMax = 214.29; // Replace with actual API max value
          
          // Clamp values to original bounds
          const adjustedMin = Math.max(originalMin, Math.min(currentMin, originalMax));
          const adjustedMax = Math.max(originalMin, Math.min(currentMax, originalMax));
          
          // Ensure min is not greater than max
          const finalMin = Math.min(adjustedMin, adjustedMax);
          const finalMax = Math.max(adjustedMin, adjustedMax);
          
          this.appliedFilterData.sqft.range = {
            minValue: finalMin,
            maxValue: finalMax
          };
        }
        break;
      case 'sqft':
        this.appliedFilterData.sqft = { range: null };
        break;
    }
    // Trigger change detection by creating a new reference
    this.appliedFilterData = { ...this.appliedFilterData };
    this.currentPage = 0;
    this.loadComprehensiveRankingData();
  }
}
